import { useEffect, useState, useContext } from 'react'

import {
  Box,
  Table,
  TableHeader,
  Text,
  Icon,
  Button,
  Input,
} from '@fairfashion/design-system'
import { useParams, useLocation } from 'react-router-dom'

import { FeedbackAlertContext } from '../../components/FeedbackAlert'
import {
  InvoiceIAParserFileStatusDTO,
  InvoiceIAParserStatusDTO,
  InvoiceParserResponseItems,
  InvoiceParserStatus,
} from '../../sdk/invoices/invoice.types'
import {
  BaseInvoiceCreateDTO,
  CompanyProviderCreateDTO,
  ItemTraceabilityCreateDTO,
} from '../../sdk/items/items.types'
import { sdk } from '../../services/sdk'

import { InvoiceItemsModal } from './components/invoice-items-modal.comp'
import * as Styles from './ia-result.styles'

export const IAResult = () => {
  const { id } = useParams<{ id: string }>()
  const { state } = useLocation()
  const [iaResponseStatus, setIAResponseStatus] =
    useState<InvoiceIAParserStatusDTO | null>(state?.iaResultData || null)
  const [loading, setLoading] = useState(!state?.iaResultData)
  const [editingData, setEditingData] = useState<Record<string, any>>({})
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceIAParserFileStatusDTO | null>(null)
  const { feedbackAlert } = useContext(FeedbackAlertContext)
  const [currentInvoiceItems, setCurrentInvoiceItems] = useState<
    Record<string, InvoiceParserResponseItems[]>
  >({})

  useEffect(() => {
    if (!iaResponseStatus) {
      fetchResultsData()
    }
  }, [id, iaResponseStatus])

  const fetchResultsData = async () => {
    setLoading(true)
    const resultsData = await sdk.invoices.getInvoiceIAParserResults()
    const found = resultsData.find((b) => String(b.processingStartDate) === id)
    setIAResponseStatus(found || null)
    setEditingData({})
    setCurrentInvoiceItems({})
    setLoading(false)
  }

  const handleEditData = (key: string, field: string, value: any) => {
    setEditingData((prev) => ({
      ...prev,
      [`${key}_${field}`]: value,
    }))
  }

  const handleCreateTraceabilityBatch = async () => {
    try {
      if (!iaResponseStatus?.results.length) return

      const originItems = iaResponseStatus.results.flatMap((result) => {
        const invoiceData: BaseInvoiceCreateDTO = {
          invoiceCode: getInputValue(
            editingData,
            `${result.fileName}_code`,
            result.data?.code,
          ),
          invoiceAccessKey: getInputValue(
            editingData,
            `${result.fileName}_accessKey`,
            result.data?.accessKey,
          ),
          invoiceCreateDate: getInputValue(
            editingData,
            `${result.fileName}_createDate`,
            result.data?.createDate,
          ),
        }

        const itemProviderRelation: CompanyProviderCreateDTO = {
          companyClientTaxId: getInputValue(
            editingData,
            `${result.fileName}_clientTaxId`,
            result.data?.clientTaxId,
          ),
          companyProviderTaxId: getInputValue(
            editingData,
            `${result.fileName}_providerTaxId`,
            result.data?.providerTaxId,
          ),
          companyProviderEmail: `admin@${getInputValue(
            editingData,
            `${result.fileName}_providerTaxId`,
            result.data?.providerTaxId,
          )}.com`,
        }

        const itemsData = currentInvoiceItems[result.fileName] ?? []

        const originItems: ItemTraceabilityCreateDTO[] = itemsData.map(
          (item, itemIndex) => {
            const itemCode = getInputValue(
              editingData,
              `${result.fileName}_item_${itemIndex}_code`,
              item.code,
            )

            const itemDescription = getInputValue(
              editingData,
              `${result.fileName}_item_${itemIndex}_description`,
              item.description,
            )

            const itemQuantity = getInputValue(
              editingData,
              `${result.fileName}_item_${itemIndex}_quantity`,
              Number(item.quantity),
            )

            const itemTaxUnit = getInputValue(
              editingData,
              `${result.fileName}_item_${itemIndex}_taxUnit`,
              item.taxUnit,
            )

            const originItem: ItemTraceabilityCreateDTO = {
              referenceId: `${result.fileName.replace(/\s/g, '')}-${itemCode}-${itemIndex}`,
              description: itemDescription,
              quantity: Number(itemQuantity),
              taxUnit: itemTaxUnit,
              code: itemCode,
              itemInvoice: invoiceData,
              itemProviderRelation,
            }

            return originItem
          },
        )

        return originItems
      })

      await sdk.items.createItemTraceabilityBatch(
        iaResponseStatus.itemsIds,
        originItems,
      )

      feedbackAlert('Rastreabilidade criada com sucesso!', 'success')
    } catch (error: any) {
      let errorMsg = ''
      if (error?.message) {
        errorMsg = ` Erro: ${error.message}`
      } else {
        errorMsg = ' Erro desconhecido.'
      }
      feedbackAlert(
        `Erro ao criar rastreabilidade. Tente novamente.${errorMsg}`,
        'error',
      )
    }
  }

  if (loading) {
    return (
      <Box width="100%" justifyContent="center" alignItems="center" padding={4}>
        <Text>Carregando...</Text>
      </Box>
    )
  }

  if (!iaResponseStatus) {
    return (
      <Box width="100%" justifyContent="center" alignItems="center" padding={4}>
        <Icon name="file" size={48} color="grey_3" />
        <Text color="grey_3">Resultados não encontrados</Text>
      </Box>
    )
  }

  const headers: TableHeader[] = [
    {
      key: 'fileName',
      label: 'Arquivo',
      minWidth: '150px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Input
          value={result.fileName || '-'}
          disabled
          width="100%"
          title={result.fileName || '-'}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Box
          padding={1}
          borderRadius="4px"
          backgroundColor={getStatusColor(result.status)}
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
        >
          <Text size="small" color="white" align="center">
            {getStatusText(result.status)}
          </Text>
        </Box>
      ),
    },
    {
      key: 'data.code',
      label: 'Código da NF',
      minWidth: '150px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Styles.RequiredInput
          value={getInputValue(
            editingData,
            `${result.fileName}_code`,
            result.data?.code || '',
          )}
          isEmpty={
            !getInputValue(
              editingData,
              `${result.fileName}_code`,
              result.data?.code || '',
            )
          }
          onChange={(e) =>
            handleEditData(result.fileName, 'code', e.target.value)
          }
          width="100%"
        />
      ),
    },
    {
      key: 'data.accessKey',
      label: 'Chave de acesso',
      minWidth: '150px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Styles.RequiredInput
          value={getInputValue(
            editingData,
            `${result.fileName}_accessKey`,
            result.data?.accessKey || '',
          )}
          isEmpty={
            !getInputValue(
              editingData,
              `${result.fileName}_accessKey`,
              result.data?.accessKey || '',
            )
          }
          onChange={(e) =>
            handleEditData(result.fileName, 'accessKey', e.target.value)
          }
          width="100%"
        />
      ),
    },
    {
      key: 'data.createDate',
      label: 'Data de criação',
      minWidth: '150px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => {
        const rawValue = getInputValue(
          editingData,
          `${result.fileName}_createDate`,
          result.data?.createDate || '',
        )

        let value = ''

        if (typeof rawValue === 'string' && rawValue) {
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(rawValue)) {
            const [d, m, y] = rawValue.split('/')
            value = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
          } else if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
            value = rawValue.slice(0, 10)
          }
        }

        return (
          <Styles.RequiredInput
            {...({ type: 'date' } as any)}
            value={value}
            isEmpty={!value}
            onChange={(e) =>
              handleEditData(result.fileName, 'createDate', e.target.value)
            }
            width="100%"
          />
        )
      },
    },
    {
      key: 'data.providerName',
      label: 'Fornecedor',
      minWidth: '200px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Input
          value={result.data?.providerName || '-'}
          disabled
          width="100%"
          title={result.data?.providerName || '-'}
        />
      ),
    },
    {
      key: 'data.providerTaxId',
      label: 'CPF/CNPJ do Fornecedor',
      minWidth: '150px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Styles.RequiredInput
          value={getInputValue(
            editingData,
            `${result.fileName}_providerTaxId`,
            result.data?.providerTaxId || '',
          )}
          isEmpty={
            !getInputValue(
              editingData,
              `${result.fileName}_providerTaxId`,
              result.data?.providerTaxId || '',
            )
          }
          onChange={(e) =>
            handleEditData(result.fileName, 'providerTaxId', e.target.value)
          }
          width="100%"
        />
      ),
    },
    {
      key: 'data.clientName',
      label: 'Cliente',
      minWidth: '200px',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Input
          value={result.data?.clientName || '-'}
          disabled
          width="100%"
          title={result.data?.clientName || '-'}
        />
      ),
    },
    {
      key: 'data.clientTaxId',
      label: 'CPF/CNPJ do Cliente',
      minWidth: '150px',
      align: 'center',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Styles.RequiredInput
          value={getInputValue(
            editingData,
            `${result.fileName}_clientTaxId`,
            result.data?.clientTaxId || '',
          )}
          isEmpty={
            !getInputValue(
              editingData,
              `${result.fileName}_clientTaxId`,
              result.data?.clientTaxId || '',
            )
          }
          onChange={(e) =>
            handleEditData(result.fileName, 'clientTaxId', e.target.value)
          }
          width="100%"
        />
      ),
    },
    {
      key: 'data.items',
      label: 'Itens',
      width: '80px',
      align: 'center',
      customRender: (result: InvoiceIAParserFileStatusDTO) => (
        <Box direction="row" alignItems="center" gap={1}>
          <Text>{result.data?.items?.length || 0}</Text>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedInvoice(result)
              setOpenInvoiceModal(true)
            }}
            title="Visualizar e editar itens"
          >
            <Icon name="eye" size={16} color="grey_2" />
          </Button>
        </Box>
      ),
    },
  ]

  function getStatusColor(status: InvoiceParserStatus) {
    switch (status) {
      case 'SUCCESS':
        return 'success'
      case 'PROCESSING':
        return 'warning'
      case 'ERROR':
        return 'error'
      default:
        return 'grey_2'
    }
  }

  function getStatusText(status: InvoiceParserStatus) {
    switch (status) {
      case 'SUCCESS':
        return 'Processado'
      case 'PROCESSING':
        return 'Processando'
      case 'ERROR':
        return 'Erro'
      default:
        return 'Desconhecido'
    }
  }

  function getInputValue(
    editingData: Record<string, any>,
    key: string,
    fallbackValue: any,
  ) {
    return key in editingData ? editingData[key] : fallbackValue
  }

  return (
    <Box width="100%" height="100%" direction="column" padding={3}>
      <Box
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        marginBottom={2}
      >
        <Button variant="outlined" onClick={fetchResultsData} loading={loading}>
          <Icon name="refresh" size={16} color="grey_1" /> Atualizar
        </Button>

        <Styles.SendTraceabilityButton
          padding="14px 16px"
          onClick={handleCreateTraceabilityBatch}
          disabled={iaResponseStatus.results.length === 0}
        >
          <Icon name="rocket" size={16} color="white" /> Enviar Rastreabilidade
        </Styles.SendTraceabilityButton>
      </Box>

      <Table
        headers={headers}
        data={iaResponseStatus.results}
        spacing="small"
      />

      <InvoiceItemsModal
        open={openInvoiceModal}
        onClose={() => setOpenInvoiceModal(false)}
        invoice={selectedInvoice}
        fileName={selectedInvoice?.fileName || ''}
        editingData={editingData}
        handleEditData={handleEditData}
        onItemsChange={(items) => {
          if (selectedInvoice?.fileName) {
            setCurrentInvoiceItems((prev) => ({
              ...prev,
              [selectedInvoice.fileName]: items,
            }))
          }
        }}
      />
    </Box>
  )
}
